import { TaskState, type UserInfo } from "@iflyrpa/share";
import type { Response } from "playwright-core";
import { ExecutionState } from "../../types";
import { ProxyAgent } from "../../utils/proxy";
import type { SteelAction } from "./index";

const scanRetryMaxCount = 60; // 二维码扫描重试次数
const waitQrcodeResultMaxTime = scanRetryMaxCount * 2 * 1000; // 最长等待时间

// 视频号助手登录
export const rpaServer: SteelAction = async (task, params) => {
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    let executionState: ExecutionState | undefined;

    let proxyUrl: string | undefined;
    if (params.localIP) {
        const args: [string, string | undefined | null, number | undefined] = [
            params.localIP,
            params.proxyLoc,
            params.accountId,
        ];
        task.logger?.info(`==> 开始获取代理信息：${args}`);
        const ProxyAgentResult = await ProxyAgent({ logger: task.logger }, ...args);
        task.logger?.info("==> 代理信息获取成功！");
        proxyUrl = ProxyAgentResult ? `http://${ProxyAgentResult.ip}:${ProxyAgentResult.port}` : undefined;
    }

    const page = await task.createPage({
        url: "https://channels.weixin.qq.com/login.html",
        proxyUrl,
        userAgent: params.userAgent,
        ...(params.viewport ? { viewport: params.viewport } : {}),
    });

    task.steelBrowser?.on("disconnected", async () => {
        const browserContext = task.steelBrowserContext;
        const browser = task.steelBrowser;
        executionState = ExecutionState.BROWSER_CLOSED;
        await page.close();

        if (browserContext) {
            await browserContext.close();
        }
        if (browser) {
            await browser.close();
        }
    });

    await updateTaskState?.({
        state: TaskState.WAIT_SCAN,
        connectAddress: await task.steelConnector?.getLive(task.sessionId || ""),
        sessionId: task.sessionId,
    });

    let token: string | undefined;
    let userInfo: UserInfo = { finderUsername: "", uniqueId: "", avatar: "", name: "" };
    let finderList: Array<{
        finderUsername: string;
        nickname: string;
        headImgUrl: string;
        uniqId: string;
        isMasterFinder: boolean;
    }> = [];

    try {
        await new Promise<void>((resolve, reject) => {
            let finished = false;

            const timer = setTimeout(async () => {
                cleanup();
                executionState = ExecutionState.TIMEOUT;
                await updateTaskState?.({
                    state: TaskState.TIMEOUT,
                    error: "等待扫码登录超时",
                });
                resolve();
            }, waitQrcodeResultMaxTime);

            const cleanup = () => {
                if (finished) return;
                finished = true;
                clearTimeout(timer);
                page.off("response", handleAfterLogin);
                page.off("response", handleScanStatus);
                task.steelBrowser?.off("disconnected", handleBrowserClosed);
            };

            const handleBrowserClosed = () => {
                cleanup();
                executionState = ExecutionState.BROWSER_CLOSED;
                resolve();
            };

            const handleAfterLogin = async (resp: Response) => {
                try {
                    const url = resp.url();

                    // 视频号助手登录成功后获取认证数据
                    if (
                        url.includes("/cgi-bin/mmfinderassistant-bin/auth/auth_data") ||
                        url.includes("/mmfinderassistant-bin/auth/auth_data")
                    ) {
                        try {
                            const jsonResponse = await resp.json();

                            if (jsonResponse.errCode === 0 && jsonResponse.data) {
                                await updateTaskState?.({
                                    state: TaskState.CONFIRMED,
                                });

                                const data = jsonResponse.data;

                                // 获取视频号列表
                                if (data.finderUser) {
                                    userInfo.name = data.finderUser.nickname || "";
                                    userInfo.uniqueId = data.finderUser.uniqId || "";
                                    userInfo.avatar = data.finderUser.headImgUrl || "";
                                    userInfo.finderUsername = data.finderUser.finderUsername || "";
                                }

                                // 如果有多个视频号，获取列表
                                if (data.finderList && Array.isArray(data.finderList)) {
                                    finderList = data.finderList.map((f: any) => ({
                                        finderUsername: f.finderUsername,
                                        nickname: f.nickname,
                                        headImgUrl: f.headImgUrl,
                                        uniqId: f.uniqId,
                                        isMasterFinder: f.isMasterFinder,
                                    }));
                                }

                                task.logger.info("auth_data 解析成功", {
                                    nickname: userInfo.name,
                                    finderCount: finderList.length,
                                });
                            }
                        } catch (e) {
                            task.logger.warn("解析 auth_data 失败:", {
                                error: e instanceof Error ? e.message : String(e),
                                url,
                            });
                        }
                    } else if (url.includes("/mmfinderassistant-bin/helper/helper_upload_params")) {
                        // 获取用户微信号
                        task.logger.info("检测到 helper_upload_params 接口");
                        cleanup();
                        try {
                            const jsonUserInfo: { data: { uin: string } } = await resp.json();
                            userInfo.uin = jsonUserInfo.data.uin ?? "";
                            task.logger.info("用户uin获取成功", { uin: userInfo.uin });
                        } catch (e) {
                            task.logger.warn("无法解析用户信息:", {
                                url,
                                error: e instanceof Error ? e.message : String(e),
                            });
                        }

                        await updateTaskState?.({
                            state: TaskState.CONFIRMED,
                        });

                        executionState = ExecutionState.SUCCESS;
                        resolve();
                    }

                    // 备选：检测登录成功后的页面跳转
                    if (url.includes("/platform") || url.includes("/live")) {
                        // 如果还没处理过，尝试从页面获取数据
                        if (executionState !== ExecutionState.SUCCESS) {
                            try {
                                await page.waitForFunction(() => !!(window as any).finder?.helperData, {
                                    timeout: 5000,
                                });

                                const helperData = await page.evaluate(() => {
                                    const win = window as unknown as {
                                        finder: {
                                            helperData: {
                                                finderList: Array<any>;
                                            };
                                        };
                                    };
                                    return win.finder.helperData;
                                });

                                if (helperData) {
                                    cleanup();
                                    finderList = helperData.finderList || [];

                                    const masterFinder = finderList.find((f) => f.isMasterFinder) || finderList[0];
                                    if (masterFinder) {
                                        userInfo = {
                                            name: masterFinder.nickname,
                                            uniqueId: masterFinder.finderUsername,
                                            avatar: masterFinder.headImgUrl,
                                            finderUsername: masterFinder.finderUsername,
                                        };
                                    }

                                    await updateTaskState?.({
                                        state: TaskState.CONFIRMED,
                                    });

                                    executionState = ExecutionState.SUCCESS;
                                    resolve();
                                }
                            } catch (e) {
                                task.logger.warn("从页面获取 helperData 失败:", { error: e });
                            }
                        }
                    }
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            };

            // 视频号助手扫码状态检测接口：auth_login_status
            // 状态码说明：
            // status: 0 = 未扫码
            // status: 5 = 已扫码未确认 (手机上点击确认登录)
            // status: 1 = 已确认登录成功
            // status: 3 = 登录成功（另一种状态）
            // acctStatus: 0 = 未登录, 1 = 已登录
            const handleScanStatus = async (response: Response) => {
                try {
                    const url = response.url();

                    // 检测视频号助手扫码状态接口
                    if (!url.includes("/mmfinderassistant-bin/auth/auth_login_status")) {
                        return;
                    }

                    let jsonResponse: Record<string, unknown>;
                    try {
                        const text = await response.text();
                        if (!text) {
                            task.logger.warn("Empty response body:", { url });
                            return;
                        }
                        jsonResponse = JSON.parse(text);
                    } catch (e) {
                        task.logger.warn("Failed to parse json:", { url, error: e });
                        return;
                    }

                    // 检查错误码
                    if (jsonResponse.errCode !== 0) {
                        task.logger.warn("auth_login_status 返回错误:", {
                            errCode: jsonResponse.errCode,
                            errMsg: jsonResponse.errMsg,
                        });
                        return;
                    }

                    const data = jsonResponse.data as Record<string, number> | undefined;
                    if (!data) return;

                    const status = data.status;
                    const acctStatus = data.acctStatus;

                    task.logger?.info(`扫码状态: status=${status}, acctStatus=${acctStatus}`);

                    // 状态判断
                    if (status === 0 && acctStatus === 0) {
                        // 未扫码，继续等待
                        return;
                    }

                    if (status === 5 && acctStatus === 1) {
                        // 已扫码，等待确认
                        await updateTaskState?.({
                            state: TaskState.SCANNED,
                        });
                        return;
                    }

                    // 登录成功状态：status=1 或 status=3
                    if ((status === 1 && acctStatus === 1) || (status === 3 && acctStatus === 0)) {
                        // 扫码完成，开始监听登录后的数据获取
                        page.off("response", handleScanStatus);
                        page.on("response", handleAfterLogin);

                        // 触发获取 auth_data
                        try {
                            await page.evaluate(() => {
                                // 触发视频号助手获取用户数据
                                const event = new CustomEvent("getAuthData");
                                window.dispatchEvent(event);
                            });
                        } catch (e) {
                            // 忽略错误，继续监听响应
                        }
                    }
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            };

            // 在页面加载之前就注册监听，防止丢失请求
            task.steelBrowser?.on("disconnected", handleBrowserClosed);
            page.on("response", handleScanStatus);
        });
    } finally {
        await page.close();
    }

    if (executionState === undefined) return { code: 500, message: "未知错误", data: {} };

    switch (executionState) {
        case ExecutionState.BROWSER_CLOSED: {
            const msg = "浏览器已关闭，任务中断!";
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: "浏览器已关闭",
            });
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.TIMEOUT: {
            const msg = "等待用户扫码登录超时!";
            await updateTaskState?.({
                state: TaskState.TIMEOUT,
                error: msg,
            });
            task.logger.info(msg);
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.SUCCESS: {
            let localStorageData: Record<string, string> = {};
            let retryCount = 0;
            const maxRetries = 10;

            task.logger.info("开始提取 localStorage 数据...");

            while (retryCount < maxRetries) {
                try {
                    localStorageData = await page.evaluate(() => ({
                        fingerPrintDeviceId: localStorage.getItem("_finger_print_device_id") || "",
                        aId: localStorage.getItem("__rx::aid") || "",
                    }));

                    // 检查是否有数据
                    if (localStorageData["fingerPrintDeviceId"] && localStorageData["aId"]) {
                        task.logger.info("视频号fingerPrintDeviceId&aId数据已获取");
                        break;
                    }

                    retryCount++;
                    task.logger.info(`等待 localStorage 数据... (${retryCount}/${maxRetries})`);
                    await page.waitForTimeout(1000);
                } catch (error) {
                    task.logger.warn(`提取 localStorage 数据失败 (尝试 ${retryCount + 1}/${maxRetries}):`, {
                        error: error instanceof Error ? error.message : String(error),
                    });
                    retryCount++;
                    await page.waitForTimeout(1000);
                }
            }

            await updateTaskState?.({
                state: TaskState.SUCCESS,
                result: {
                    cookie: JSON.stringify(await task.steelBrowserContext?.cookies()),
                    token, // 视频号助手使用 sessionId 作为主要凭证
                    userInfo: userInfo,
                    finderList, // 返回所有可管理的视频号列表
                    extraParam: { ...localStorageData, uin: userInfo.uin, finderUserName: userInfo.finderUsername }, // 添加 localStorage 数据和 uin
                },
            });
            return {
                code: 0,
                message: "成功",
                data: {
                    cookie: JSON.stringify(await task.steelBrowserContext?.cookies()),
                    token,
                    userInfo,
                    finderList, // 包含所有视频号信息，支持多账号管理
                    extraParam: { ...localStorageData, uin: userInfo.uin, finderUserName: userInfo.finderUsername }, // 添加 localStorage 数据和 uin
                },
            };
        }
        default:
            return { code: 500, message: "任务执行中断", data: {} };
    }
};
